import React, { useMemo, useState, FunctionComponent, Fragment, useCallback } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import classNames from 'classnames';
import { isEqual } from 'lodash-es';
import Button, { ButtonProps } from '../../atoms/button';
import Text from '../../atoms/text';
import InputStyleWrapper, { InputStyle } from '../../atoms/inputStyleWrapper';
import { AlignEnum, ColorNameEnum, SizeEnum, WeightEnum } from '../../types';
import AutosizeTextarea from '../../atoms/autosizeTextarea';
import Tooltip from '../../atoms/tooltip';

import './index.less';
import { InputValidationMessage } from '../..';

const TooltipPropsTypes = {
  content: PropTypes.string,
  placement: PropTypes.oneOf(['top', 'bottom', 'right', 'left']),
};

const ItemPropTypes = {
  value: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  title: PropTypes.string, // Sets the button text
  titleAttribute: PropTypes.string, // Sets the button's HTML title attribute (native browser tooltip)
  selectClassName: PropTypes.string,
  icon: PropTypes.string,
  onClick: PropTypes.func,
  onSelect: PropTypes.func,
  checkbox: PropTypes.bool,
  disabled: PropTypes.bool,
  textInputValue: PropTypes.string,
  textInputRequiredMessage: PropTypes.string,
  tooltip: PropTypes.shape(TooltipPropsTypes),
  // Rendered as a child node (`<span>{item.textHtml}</span>`), so it accepts any
  // renderable node (string, element, …), not just a string. `func` kept for
  // back-compat with callers that pass a render function.
  textHtml: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  role: PropTypes.string,
};

export const ButtonGroupPropTypes = {
  items: PropTypes.arrayOf(PropTypes.shape(ItemPropTypes).isRequired).isRequired,
  selectPlaceholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  className: PropTypes.string,
  itemClassName: PropTypes.string,
  direction: PropTypes.oneOf(['row', 'column', 'default']),
  onChange: PropTypes.func,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  searchStopPropagation: PropTypes.bool,
  showCheckbox: PropTypes.bool,
  showTextInput: PropTypes.bool,
  textInputPlaceholder: PropTypes.string,
  onTextInputChange: PropTypes.func,
  shouldColorSelectedItems: PropTypes.bool,
  pinnedItemsTitle: PropTypes.string,
  topPinnedItems: PropTypes.arrayOf(PropTypes.shape(ItemPropTypes).isRequired),
  noResultsMessage: PropTypes.string,
  noGaps: PropTypes.bool,
  shouldUseAutoSizeInput: PropTypes.bool,
  textInputRequiredMessage: PropTypes.string,
  role: PropTypes.string,
};

export type ButtonGroupProps = InferProps<typeof ButtonGroupPropTypes> &
  Pick<ButtonProps, 'styleOverrides'> &
  Omit<ButtonProps, 'onChange'>;

const GroupItem = ({
  item,
  showCheckbox,
  value,
  itemClassName,
  shouldColorSelectedItems = true,
  clickHandler,
  showTextInput,
  textInputPlaceholder,
  onTextInputChange,
  maxLength,
  shouldUseAutoSizeInput = false,
  textInputRequiredMessage,
  role = 'radio',
  rest,
}) => {
  const selected = showCheckbox ? value.includes(item.value) : isEqual(value, item.value);
  const textLength = item.textInputValue?.length ?? 0;

  return (
    <Fragment key={item.value}>
      <Button
        role={role}
        title={item.titleAttribute}
        className={classNames(
          item.value,
          itemClassName,
          { showCheckbox },
          { buttonGroupSelected: selected },
          { 'selected-no-bg': !shouldColorSelectedItems },
          { 'show-tooltip': item.tooltip },
        )}
        onClick={(e) =>
          clickHandler(e, item.value, item.onClick, item.onClickCallback, item.onSelect)
        }
        buttonType="link"
        value={item.value}
        selected={selected}
        aria-selected={role === 'tab' ? selected : null}
        aria-checked={role === 'checkbox' || role === 'radio' ? selected : null}
        disabled={!!item.disabled}
        {...rest}
      >
        {item.tooltip && (
          <Tooltip
            tooltipContent={item.tooltip.content}
            referenceClassName="button-group-item-tooltip-ref"
            className="button-group-item-tooltip"
            bgColor="light"
            placement={item.tooltip.placement}
          />
        )}
        {item.title ? <span className="item-title">{item.title}</span> : undefined}
        {item.checkbox !== undefined && (
          <input
            type="checkbox"
            checked={!!item.checkbox}
            onClick={(e) => clickHandler(e, item.value, item.onClick, item.onClickCallback)}
          />
        )}
        {showCheckbox && <div className="inline-checkbox" />}
        {item.icon && <i className={`item-icon ${item.icon}`} />}

        {item.textHtml ? <span>{item.textHtml}</span> : <span>{item.text}</span>}

        {item.secondaryDescription}
        {item.comment && <span className="comment"> / {item.comment}</span>}
      </Button>
      {showTextInput && (
        <div className="btn-group-inline-input">
          {shouldUseAutoSizeInput ? (
            <>
              <InputStyleWrapper className="autosize-textarea-input" inputStyle={InputStyle.clean}>
                <div className="input">
                  <AutosizeTextarea
                    placeholder={textInputPlaceholder}
                    onTextInputChange={(textInputValue) => {
                      onTextInputChange(textInputValue);
                    }}
                    maxLength={maxLength}
                  />
                </div>
                {maxLength && (
                  <div className="character-counter">
                    <text className={textLength < maxLength ? 'length' : 'length error'}>
                      {textLength}/{maxLength}
                    </text>
                  </div>
                )}
                {textInputRequiredMessage && (
                  <InputValidationMessage className="text-input-required-message">
                    {textInputRequiredMessage}
                  </InputValidationMessage>
                )}
              </InputStyleWrapper>
            </>
          ) : (
            <>
              <InputStyleWrapper inputStyle={InputStyle.clean} key={item.value}>
                <input
                  type="text"
                  placeholder={textInputPlaceholder ?? ''}
                  value={item.textInputValue ?? ''}
                  maxLength={maxLength}
                  onChange={(e) => onTextInputChange?.(e.target.value)}
                />
                {maxLength && (
                  <text className={textLength < maxLength ? 'length' : 'length error'}>
                    {textLength}/{maxLength}
                  </text>
                )}
              </InputStyleWrapper>
            </>
          )}
        </div>
      )}
    </Fragment>
  );
};

const Index: FunctionComponent<ButtonGroupProps> = ({
  items,
  value,
  className,
  itemClassName,
  direction,
  selectPlaceholder,
  onChange,
  searchable,
  searchPlaceholder,
  searchStopPropagation,
  showCheckbox,
  showTextInput,
  textInputPlaceholder,
  onTextInputChange,
  shouldColorSelectedItems = true,
  pinnedItemsTitle,
  topPinnedItems,
  maxLength,
  noGaps,
  noResultsMessage,
  shouldUseAutoSizeInput,
  textInputRequiredMessage,
  onSelect,
  role = 'radio',
  ...rest
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filterItems = useCallback(
    (itemsToFilter) => {
      if (!itemsToFilter) return [];

      const normalizedSearchTerm = searchTerm.trim().toLowerCase();

      if (!searchable || searchTerm === '') return itemsToFilter;

      return itemsToFilter.filter(({ text }) => text.toLowerCase().includes(normalizedSearchTerm));
    },
    [searchable, searchTerm],
  );

  const filteredItems = useMemo(() => filterItems(items), [items, filterItems]);

  const filteredTopItems = useMemo(
    () => filterItems(topPinnedItems),
    [topPinnedItems, filterItems],
  );

  const clickHandler = async (
    e: React.MouseEvent,
    option: number | string,
    onClick?: (...args: any) => any,
    onClickCallback?: {
      onStart?: () => void;
      onSuccess?: () => void;
      onError?: () => void;
      onFinish?: (...args: any) => void;
    },
    onSelect?: () => void,
  ) => {
    if (onClick) {
      let result;
      try {
        onClickCallback?.onStart?.();
        result = await onClick(option);
        onClickCallback?.onSuccess?.();
        // eslint-disable-next-line unicorn/prefer-optional-catch-binding
      } catch (error) {
        onClickCallback?.onError?.();
      } finally {
        onClickCallback?.onFinish?.(result);
      }
    }
    e.preventDefault();
    if (onChange) await onChange(option);
    if (onSelect) onSelect();
  };

  const onTextInputClick = (event: React.MouseEvent) => {
    if (searchStopPropagation) {
      event.stopPropagation();
    }
  };

  const onRightIconClick = (event: React.MouseEvent) => {
    if (searchStopPropagation) {
      event.stopPropagation();
    }
    setSearchTerm('');
  };

  return (
    <div>
      {searchable && (
        <InputStyleWrapper
          inputStyle={InputStyle.pillSm}
          leftIcon={<i className="icon-search-2" />}
          rightIcon={searchTerm ? <i className="icon-x clear-icon" /> : undefined}
          rightIconOnClick={onRightIconClick}
          className="clickable"
        >
          <div className="input">
            <input
              onChange={(e) => setSearchTerm(e.target.value)}
              value={searchTerm}
              placeholder={searchPlaceholder || ''}
              type="text"
              onClick={onTextInputClick}
            />
          </div>
        </InputStyleWrapper>
      )}
      <div
        data-id="button-group"
        className={classNames('icon-list', className, {
          'column-list': direction === 'column',
          'no-gaps': noGaps,
        })}
        role={role === 'radio' ? 'radiogroup' : 'group'}
      >
        {filteredTopItems?.length > 0 && (
          <>
            {pinnedItemsTitle && (
              <Text
                text={pinnedItemsTitle}
                size={SizeEnum['12px']}
                colorName={ColorNameEnum.extraDark}
                weight={WeightEnum.bold}
                align={AlignEnum.left}
                className={classNames('pinned-items-title')}
              />
            )}
            {filteredTopItems.map((item) => (
              <GroupItem
                // TODO SAM-74443 The key overflows the limit of characters in the key (This is risky since the key won't be necessarily unique)
                key={`filter-top-${item.value}`}
                item={item}
                showCheckbox={showCheckbox}
                value={value}
                itemClassName={classNames(itemClassName, 'top-pinned-items')}
                clickHandler={clickHandler}
                showTextInput={showTextInput}
                textInputPlaceholder={textInputPlaceholder}
                onTextInputChange={onTextInputChange}
                maxLength={maxLength}
                shouldUseAutoSizeInput={shouldUseAutoSizeInput}
                textInputRequiredMessage={textInputRequiredMessage}
                rest={rest}
                role={role}
              />
            ))}
          </>
        )}
        {filteredItems?.length > 0
          ? filteredItems.map((item) => (
              <GroupItem
                // TODO SAM-74443 The key overflows the limit of characters in the key (This is risky since the key won't be necessarily unique)
                key={`filtered-item-${item.text}-${item.value}`}
                item={item}
                showCheckbox={showCheckbox}
                value={value}
                itemClassName={itemClassName}
                shouldColorSelectedItems={shouldColorSelectedItems}
                clickHandler={clickHandler}
                showTextInput={showTextInput}
                textInputPlaceholder={textInputPlaceholder}
                onTextInputChange={onTextInputChange}
                shouldUseAutoSizeInput={shouldUseAutoSizeInput}
                textInputRequiredMessage={textInputRequiredMessage}
                rest={rest}
                maxLength={maxLength}
                role={role}
              />
            ))
          : noResultsMessage && <div className="button-group-no-results">{noResultsMessage}</div>}
      </div>
    </div>
  );
};

Index.propTypes = ButtonGroupPropTypes;

Index.defaultProps = {
  className: undefined,
  direction: 'default',
  selectPlaceholder: '',
  onChange: () => {},
  searchPlaceholder: undefined,
  textInputPlaceholder: '',
  onTextInputChange: () => {},
  noGaps: false,
  noResultsMessage: 'No results',
};

export default Index;
